import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import CenteredContent from '@cohort/wallet/components/layout/CenteredContent';
import {Spinner} from '@cohort/wallet/components/Spinner';
import TitleWithLeadingIcon from '@cohort/wallet/components/TitleWithLeadingIcon';
import useNotify from '@cohort/wallet/hooks/notify';
import {useUserStore} from '@cohort/wallet/hooks/stores/user';
import {useApi} from '@cohort/wallet/hooks/useApi';
import useShopify from '@cohort/wallet/hooks/useShopify';
import useStoreContext from '@cohort/wallet/hooks/useStoreContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import StoreLeftPanel from '@cohort/wallet/layouts/store/StoreLeftPanel';
import StorePageContainer from '@cohort/wallet/layouts/store/StorePage';
import {OrdersApi} from '@cohort/wallet/lib/Endpoints';
import {getDigitalAssetsListRoute, getStoreCampaignRoute} from '@cohort/wallet/lib/Pages';
import {canShare, shareNft} from '@cohort/wallet/lib/Share';
import type {OrderWDto} from '@cohort/wallet-schemas/order';
import {Check, X} from '@phosphor-icons/react';
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate, useParams} from 'react-router-dom';

interface OrderNavigationState {
  order?: OrderWDto;
}

interface OrderSuccessProps {
  order: OrderWDto;
}

const OrderSuccess: React.FC<OrderSuccessProps> = ({order}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const {campaign} = useStoreContext();
  const {backgroundColor, accentColor} = useThemeContext();
  const shopify = useShopify();
  const navigate = useNavigate();
  const {t} = useTranslation('pages', {keyPrefix: 'store.orderPage'});
  const notify = useNotify();

  const onHandleGotoSpace = useCallback(() => {
    setIsProcessing(true);
    setTimeout(() => {
      if (shopify.active) {
        shopify.spaceRedirect();
      } else {
        navigate(getDigitalAssetsListRoute(), {replace: true});
      }
    }, 1000);
  }, [navigate, shopify]);

  const handleShare = useCallback(() => {
    setIsSharing(true);
    shareNft(
      campaign.merchant.name,
      campaign.collection.name,
      order.shareImageUrl,
      'orderConfirmationModal',
      {
        orderId: order.id,
        campaignId: campaign.id,
        tokenId: order.tokenId,
      }
    )
      .catch(() => notify('error', t('shareError')))
      .finally(() => {
        setIsSharing(false);
      });
  }, [
    t,
    notify,
    campaign.id,
    campaign.merchant.name,
    campaign.collection.name,
    order.id,
    order.shareImageUrl,
    order.tokenId,
  ]);

  return (
    <TitleWithLeadingIcon
      testId={canShare() ? 'share' : 'go-to-space'}
      title={t('orderConfirmedTitle')}
      subtitle={t('orderConfirmedContent')}
      icon={<Check className="h-11 w-11" style={{color: accentColor}} />}
      {...(canShare() && {
        additionalContent: (
          <PrimaryButton
            isLoading={isProcessing}
            text={t('orderConfirmedButton')}
            testId="go-to-space-cta"
            onClick={onHandleGotoSpace}
            className="mt-3 w-full"
            tracking={{namespace: 'store.orderPage.success'}}
          />
        ),
      })}
    >
      <PrimaryButton
        data-testid="transfer-in-progress-cta"
        onClick={() => {
          canShare() ? handleShare() : onHandleGotoSpace();
        }}
        text={
          (canShare() && isSharing) || (!canShare() && isProcessing) ? (
            <Spinner color={backgroundColor} height="h-5" width="w-5" />
          ) : canShare() ? (
            t('orderConfirmedShareButton')
          ) : (
            t('orderConfirmedButton')
          )
        }
        type="submit"
        className="w-full md:w-4/5"
        tracking={{namespace: 'store.orderPage.success'}}
      />
    </TitleWithLeadingIcon>
  );
};

const OrderPage: React.FC = () => {
  const user = useUserStore(store => store.user);
  const {campaign} = useStoreContext();
  const {accentColor} = useThemeContext();

  const navigate = useNavigate();
  const getOrder = useApi(OrdersApi.getOrder);
  const {orderId} = useParams();
  const {t} = useTranslation('pages', {keyPrefix: 'store.orderPage'});
  const location = useLocation();

  const [order, setOrder] = useState<OrderWDto>();

  useEffect(() => {
    if (user === null) {
      navigate(getStoreCampaignRoute(campaign.store.slug), {replace: true});
      return;
    }

    const orderNavigationState = location.state as OrderNavigationState | null;
    if (orderNavigationState?.order) {
      setOrder(orderNavigationState.order);
    } else {
      getOrder(orderId ?? '')
        .then(order => {
          setOrder(order);
        })
        .catch(() => navigate(getStoreCampaignRoute(campaign.store.slug), {replace: true}));
    }
  }, [campaign, getOrder, location.state, navigate, orderId, user]);

  const renderOrder = useCallback(() => {
    switch (order?.status) {
      case 'success':
      case 'processing':
        return <OrderSuccess order={order} />;
      case 'error':
        return (
          <TitleWithLeadingIcon
            testId="order-error"
            title={t('orderErrorTitle')}
            subtitle={t('orderErrorSubtitle')}
            icon={<X className="h-11 w-11" style={{color: accentColor}} />}
          >
            <PrimaryButton
              data-testid="transfer-in-progress-cta"
              onClick={() => {
                navigate(getStoreCampaignRoute(campaign.store.slug), {replace: true});
              }}
              text={t('orderErrorCtaText')}
              type="submit"
              tracking={{namespace: 'store.orderPage.error'}}
            />
          </TitleWithLeadingIcon>
        );
      default:
        navigate(getStoreCampaignRoute(campaign.store.slug), {replace: true});
        return null;
    }
  }, [accentColor, campaign, navigate, order, t]);

  return (
    <StorePageContainer
      title={order?.status === 'error' ? t('orderErrorTitle') : t('orderConfirmedTitle')}
      tracking={{
        namespace: 'store.orderPage',
        metadata: {
          orderId: order?.id,
        },
      }}
      leftPanel={<StoreLeftPanel />}
    >
      <CenteredContent className="max-w-xl px-4 md:px-0">{renderOrder()}</CenteredContent>
    </StorePageContainer>
  );
};

export default OrderPage;
